import { FGListen, FieldGroup, FieldSet, useApiEffect } from "nsitools-react";
import * as React from "react";
import { Link, useHistory } from "react-router-dom";
import * as Yup from "yup";

import { CountryApi, PersonApi, SplitPersonDto } from "../../../../../api";
import {
  FGSRSHCheckboxInput,
  InformationCard,
  SmallFormGenerator,
} from "../../../../../components";
import { ERoutes } from "../../../../../ERoutes";
import {
  useApiService,
  useSaveApi,
  useTl,
  useWindow,
} from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { vatNumberValidate } from "../../../../../utils";
import { PersonSplitForm } from "./PersonSplitForm";

export interface IPersonSplitProps {
  originalPersonId: number;
}

export const PersonSplit: React.FunctionComponent<IPersonSplitProps> = ({
  originalPersonId,
}) => {
  const api = useApiService(PersonApi);
  const countryApi = useApiService(CountryApi);

  const { t } = useTl();

  const [originalEmail, setOriginalEmail] = React.useState<string>();
  const [copyEmail, setCopyEmail] = React.useState<string>();
  const { openNewTab } = useWindow();
  const history = useHistory();

  /* ========== Getting Data ===========*/
  const [data, loading] = useApiEffect(() =>
    api.getSplitPerson(originalPersonId)
  );

  React.useEffect(() => {}, []);

  const { saving, saveItem } = useSaveApi(
    React.useMemo(
      () => ({
        saveApiFn: async (d: SplitPersonDto) => {
          const result = await api.saveSplitPerson(d);
          openNewTab(`${ERoutes.people}/${d.originalId}/detail/view`);
          history.push(`${ERoutes.people}/${result}/detail/view`);
        },
      }),
      [api, history, openNewTab]
    )
  );

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      originalPerson: Yup.object().shape(
        {
          firstName: Yup.string().nullable().required(t(ETLCodes.Required)),
          lastName: Yup.string().nullable().required(t(ETLCodes.Required)),
          languageId: Yup.string().nullable().required(t(ETLCodes.Required)),
          personHonorificId: Yup.string()
            .nullable()
            .required(t(ETLCodes.Required)), //number().required(),
          street: Yup.string().nullable().required(t(ETLCodes.Required)),
          number: Yup.string().nullable().required(t(ETLCodes.Required)),
          postalCode: Yup.string().nullable().required(t(ETLCodes.Required)),
          countryId: Yup.number()
            .nullable()
            .required(t(ETLCodes.Required))
            .test(
              "is-vat-be",
              t(ETLCodes.VATNotCorrespondingToCountry),
              async function (value) {
                if (!this.parent.vatNumber) return true;
                const belgium = await countryApi.isBelgian(value);
                return (
                  !belgium.value ||
                  (belgium.value && this.parent.vatNumber.substr(0, 2) === "BE")
                );
              }
            ),
          city: Yup.string().nullable().required(t(ETLCodes.Required)),
          email: Yup.string()
            .nullable()
            .test("required", t(ETLCodes.Required), async function (value) {
              if (
                (this.parent.phoneNumber === undefined ||
                  this.parent.phoneNumber === null) &&
                (this.parent.mobilePhoneNumber === undefined ||
                  this.parent.mobilePhoneNumber === null)
              ) {
                return value !== null && value !== undefined;
              }
              return true;
            })
            .test("unique", t(ETLCodes.MustBeUnique), async function (value) {
              if (value !== null && value !== undefined) {
                return originalEmail !== copyEmail;
              }
              return true;
            }),
          phoneNumber: Yup.string()
            .nullable()
            .test("required", t(ETLCodes.Required), async function (value) {
              if (
                (this.parent.email === undefined ||
                  this.parent.email === null) &&
                (this.parent.mobilePhoneNumber === undefined ||
                  this.parent.mobilePhoneNumber === null)
              ) {
                return value !== null && value !== undefined;
              }
              return true;
            }),
          mobilePhoneNumber: Yup.string()
            .nullable()
            .test("required", t(ETLCodes.Required), async function (value) {
              if (
                (this.parent.phoneNumber === undefined ||
                  this.parent.phoneNumber === null) &&
                (this.parent.email === undefined || this.parent.email === null)
              ) {
                return value !== null && value !== undefined;
              }
              return true;
            }),
          vatNumber: Yup.string()
            .nullable()
            .test("validate", t(ETLCodes.InvalidVAT), async function (value) {
              if (!this.parent.countryId) return true;
              const belgium = await countryApi.isBelgian(this.parent.countryId);
              if (belgium.value) return vatNumberValidate(value);
              return true;
            }),
        },
        [["mobilePhoneNumber", "phoneNumber"]]
      ),
      copyPerson: Yup.object().shape(
        {
          firstName: Yup.string().nullable().required(t(ETLCodes.Required)),
          lastName: Yup.string().nullable().required(t(ETLCodes.Required)),
          languageId: Yup.number().nullable().required(t(ETLCodes.Required)),
          personHonorificId: Yup.string()
            .nullable()
            .required(t(ETLCodes.Required)), //number().required(),
          street: Yup.string().nullable().required(t(ETLCodes.Required)),
          number: Yup.string().nullable().required(t(ETLCodes.Required)),
          postalCode: Yup.string().nullable().required(t(ETLCodes.Required)),
          countryId: Yup.number()
            .nullable()
            .required(t(ETLCodes.Required))
            .test(
              "is-vat-be",
              t(ETLCodes.VATNotCorrespondingToCountry),
              async function (value) {
                if (!this.parent.vatNumber) return true;
                const belgium = await countryApi.isBelgian(value);
                return (
                  !belgium.value ||
                  (belgium.value && this.parent.vatNumber.substr(0, 2) === "BE")
                );
              }
            ),
          city: Yup.string().nullable().required(t(ETLCodes.Required)),
          email: Yup.string()
            .nullable()
            .test("required", t(ETLCodes.Required), async function (value) {
              if (
                (this.parent.phoneNumber === undefined ||
                  this.parent.phoneNumber === null) &&
                (this.parent.mobilePhoneNumber === undefined ||
                  this.parent.mobilePhoneNumber === null)
              ) {
                return value !== null && value !== undefined;
              }
              return true;
            })
            .test("unique", t(ETLCodes.MustBeUnique), async function (value) {
              if (value !== null && value !== undefined) {
                return originalEmail !== copyEmail;
              }
              return true;
            }),
          phoneNumber: Yup.string()
            .nullable()
            .test("required", t(ETLCodes.Required), async function (value) {
              if (
                (this.parent.email === undefined ||
                  this.parent.email === null) &&
                (this.parent.mobilePhoneNumber === undefined ||
                  this.parent.mobilePhoneNumber === null)
              ) {
                return value !== null && value !== undefined;
              }
              return true;
            }),
          mobilePhoneNumber: Yup.string()
            .nullable()
            .test("required", t(ETLCodes.Required), async function (value) {
              if (
                (this.parent.phoneNumber === undefined ||
                  this.parent.phoneNumber === null) &&
                (this.parent.email === undefined || this.parent.email === null)
              ) {
                return value !== null && value !== undefined;
              }
              return true;
            }),
          vatNumber: Yup.string()
            .nullable()
            .test("validate", t(ETLCodes.InvalidVAT), async function (value) {
              if (!this.parent.countryId) return true;
              const belgium = await countryApi.isBelgian(this.parent.countryId);
              if (belgium.value) return vatNumberValidate(value);
              return true;
            }),
        },
        [["mobilePhoneNumber", "phoneNumber"]]
      ),
    });
  }, [copyEmail, countryApi, originalEmail, t]);

  return (
    <SmallFormGenerator
      initialValues={data}
      onSubmit={saveItem}
      editMode={true}
      loading={loading}
      saving={saving}
      validationSchema={FormSchema}
    >
      <FieldGroup columns={2}>
        <FGListen
          field="originalPerson.email"
          onChanged={(value) => setOriginalEmail(value)}
        />
        <FGListen
          field="copyPerson.email"
          onChanged={(value) => setCopyEmail(value)}
        />
        <FieldGroup>
          <FieldSet
            title={
              <Link
                target="_blank"
                to={`${ERoutes.people}/${data?.originalId}/detail/view`}
              >
                {t(ETLCodes.SplitPerson)}
              </Link>
            }
          />
          <PersonSplitForm
            person={data?.originalPerson}
            personId={data?.originalId}
            fgName={"originalPerson"}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldSet title={t(ETLCodes.NewPerson)} />
          <PersonSplitForm
            person={data?.copyPerson}
            personId={0}
            fgName={"copyPerson"}
          />
        </FieldGroup>
      </FieldGroup>
      <div style={{ marginTop: "2rem" }}>
        <InformationCard text={t(ETLCodes.PersonSplitBCMessage)} />
        <FieldSet title={t(ETLCodes.Dogs)}>
          <InformationCard text={t(ETLCodes.PersonSplitDogsMessage)} />
        </FieldSet>
        <FieldSet title={t(ETLCodes.WebAccess)}>
          <InformationCard text={t(ETLCodes.PersonSplitWebPortalMessage)} />
        </FieldSet>
        <FieldSet title={t(ETLCodes.Requests)}>
          <InformationCard text={t(ETLCodes.PersonSplitRequestsMessage)} />
        </FieldSet>
        <FieldSet title={t(ETLCodes.Member)}>
          <FGSRSHCheckboxInput
            name="copyPersonMember"
            label={t(ETLCodes.LinkToNewPerson)}
          />
        </FieldSet>
        <FieldSet title={t(ETLCodes.Breeder)}>
          <InformationCard text={t(ETLCodes.PersonSplitKennelsMessage)} />
          <InformationCard text={t(ETLCodes.PersonSplitBreederDogsMessage)} />
        </FieldSet>
        <FieldSet title={t(ETLCodes.Judge)}>
          <FGSRSHCheckboxInput
            name="copyPersonJudge"
            label={t(ETLCodes.LinkToNewPerson)}
          />
        </FieldSet>
        <FieldSet title={t(ETLCodes.Veterinarian)}>
          <FGSRSHCheckboxInput
            name="copyPersonVet"
            label={t(ETLCodes.LinkToNewPerson)}
          />
        </FieldSet>
      </div>
    </SmallFormGenerator>
  );
};
